import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Node } from "@babylonjs/core/node";

/**
 * Get all meshes for a transform hierarchy.
 * Includes the root itself if it's a mesh, plus all descendant meshes.
 *
 * Used by Renderer (for slot pinning) and MaterialPreviews (for slot detection).
 * Both must use this same function to guarantee index consistency.
 *
 * The returned order matches Babylon's getChildMeshes(false) pre-order walk of
 * _children insertion order. For glTF models, this matches the glTF primitive
 * traversal order, so indices correspond to glTF material slot indices. This is
 * relied upon by RendererData.targetMaterialIndex for cross-engine compatibility.
 *
 * `pruneRoots` subtrees are skipped entirely (root and descendants). Child
 * SceneObjects parent under this transform, so without pruning their meshes
 * would be enumerated as if this object owned them — see
 * SceneObject.getOwnedMeshes(), which supplies the child roots.
 */
export function getObjectMeshes(
    transform: TransformNode,
    pruneRoots?: ReadonlySet<Node>,
): AbstractMesh[] {
    const meshes: AbstractMesh[] = [];
    if (transform instanceof AbstractMesh) {
        meshes.push(transform);
    }
    if (!pruneRoots || pruneRoots.size === 0) {
        meshes.push(...transform.getChildMeshes(false));
        return meshes;
    }
    // Manual pre-order walk replicating getChildMeshes(false) ordering
    // (push each child if it's a mesh, then descend), pruning foreign subtrees.
    const visit = (node: Node): void => {
        for (const child of node.getChildren()) {
            if (pruneRoots.has(child)) continue;
            if (child instanceof AbstractMesh) meshes.push(child);
            visit(child);
        }
    };
    visit(transform);
    return meshes;
}
